// Bezier curve flatteners: cubic, quadratic, elliptical arc.
// Adaptive subdivision to a flatness tolerance; returns dense polyline.
// Zero deps, pure functions.

const FLATNESS = 0.5; // px — subdivide until segment within this of straight line

function dist(a, b) {
  const dx = a.x - b.x, dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function distPtToLine(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return dist(p, a);
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  const tc = Math.max(0, Math.min(1, t));
  const proj = { x: a.x + tc * dx, y: a.y + tc * dy };
  return dist(p, proj);
}

// Adaptive cubic Bezier subdivision via de Casteljau.
// Returns polyline including p0 and p3.
export function flattenCubic(x0, y0, x1, y1, x2, y2, x3, y3, flatness = FLATNESS) {
  const out = [{ x: x0, y: y0 }];
  function recurse(p0, p1, p2, p3, depth) {
    const a = distPtToLine(p1, p0, p3);
    const b = distPtToLine(p2, p0, p3);
    if ((a < flatness && b < flatness) || depth > 16) {
      out.push({ x: p3.x, y: p3.y });
      return;
    }
    // de Casteljau split at t=0.5
    const m01 = mid(p0, p1), m12 = mid(p1, p2), m23 = mid(p2, p3);
    const m012 = mid(m01, m12), m123 = mid(m12, m23);
    const m0123 = mid(m012, m123);
    recurse(p0, m01, m012, m0123, depth + 1);
    recurse(m0123, m123, m23, p3, depth + 1);
  }
  recurse({ x: x0, y: y0 }, { x: x1, y: y1 }, { x: x2, y: y2 }, { x: x3, y: y3 }, 0);
  return out;
}

export function flattenQuadratic(x0, y0, x1, y1, x2, y2, flatness = FLATNESS) {
  // Promote quad to cubic and reuse: cubic ctrl pts are p0+2/3*(p1-p0), p2+2/3*(p1-p2)
  const cx1 = x0 + (2 / 3) * (x1 - x0);
  const cy1 = y0 + (2 / 3) * (y1 - y0);
  const cx2 = x2 + (2 / 3) * (x1 - x2);
  const cy2 = y2 + (2 / 3) * (y1 - y2);
  return flattenCubic(x0, y0, cx1, cy1, cx2, cy2, x2, y2, flatness);
}

// SVG elliptical arc → polyline. Implements the F.6.5 endpoint-to-center conversion.
export function flattenArc(x1, y1, rx, ry, rot, laf, sf, x2, y2, flatness = FLATNESS) {
  if (rx === 0 || ry === 0) return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  const phi = (rot * Math.PI) / 180;
  const cosPhi = Math.cos(phi), sinPhi = Math.sin(phi);
  // Step 1: midpoint xform
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p = cosPhi * dx + sinPhi * dy;
  const y1p = -sinPhi * dx + cosPhi * dy;
  // Step 2: radii correction
  let rx2 = rx * rx, ry2 = ry * ry;
  const x1p2 = x1p * x1p, y1p2 = y1p * y1p;
  const lambda = x1p2 / rx2 + y1p2 / ry2;
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s; ry *= s; rx2 = rx * rx; ry2 = ry * ry;
  }
  // Step 3: center (cx', cy')
  const sign = laf === sf ? -1 : 1;
  const num = rx2 * ry2 - rx2 * y1p2 - ry2 * x1p2;
  const den = rx2 * y1p2 + ry2 * x1p2;
  const factor = Math.sqrt(Math.max(0, num / den)) * sign;
  const cxp = factor * ((rx * y1p) / ry);
  const cyp = factor * (-(ry * x1p) / rx);
  // Step 4: center in original space
  const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;
  // Step 5: angle1, deltaAngle
  function ang(ux, uy, vx, vy) {
    const dot = ux * vx + uy * vy;
    const len = Math.sqrt(ux * ux + uy * uy) * Math.sqrt(vx * vx + vy * vy);
    let a = Math.acos(Math.max(-1, Math.min(1, dot / len)));
    if (ux * vy - uy * vx < 0) a = -a;
    return a;
  }
  const theta1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dtheta = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sf && dtheta > 0) dtheta -= 2 * Math.PI;
  if (sf && dtheta < 0) dtheta += 2 * Math.PI;
  // Sample along the arc; segment count proportional to arc length / flatness
  const r = (rx + ry) / 2;
  const arcLen = Math.abs(dtheta) * r;
  const n = Math.max(8, Math.ceil(arcLen / Math.max(flatness, 0.5)));
  const out = [];
  for (let i = 0; i <= n; i++) {
    const t = theta1 + (dtheta * i) / n;
    const x = cosPhi * rx * Math.cos(t) - sinPhi * ry * Math.sin(t) + cx;
    const y = sinPhi * rx * Math.cos(t) + cosPhi * ry * Math.sin(t) + cy;
    out.push({ x, y });
  }
  return out;
}

function mid(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
